import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "dropZone", "fileInput", "previewContainer", "previewImage", "fileInfo",
    "submitButton", "errorContainer", "errorMessage", "progressContainer", 
    "progressBar", "dropText"
  ]

  connect() {
    console.log("Photo upload controller connected")
    this.selectedFile = null
    this.maxFileSize = 5 * 1024 * 1024 // 5MB
    this.allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
  }

  // File dialog methods - no longer needed, file input is directly clickable

  handleFileSelect(event) {
    const file = event.target.files[0]
    if (file) {
      this.processFile(file)
    }
  }

  // Drag and drop methods
  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()
  }

  handleDragEnter(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.add("border-gray-500", "bg-gray-50")
    this.dropTextTarget.textContent = "Drop photo here"
  }

  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.remove("border-gray-500", "bg-gray-50")
    this.dropTextTarget.textContent = "Drag photo here or click to browse"
  }

  handleDrop(event) {
    event.preventDefault()
    event.stopPropagation()
    
    this.dropZoneTarget.classList.remove("border-gray-500", "bg-gray-50")
    this.dropTextTarget.textContent = "Drag photo here or click to browse"
    
    const files = event.dataTransfer.files
    if (files.length > 0) {
      this.processFile(files[0])
    }
  }

  // File processing
  processFile(file) {
    console.log("Processing file:", file.name, file.type, file.size)
    
    // Clear previous errors
    this.hideError()
    
    // Validate file
    if (!this.validateFile(file)) {
      return
    }
    
    this.selectedFile = file
    this.fileInputTarget.files = this.createFileList(file)
    this.showPreview(file)
    this.enableSubmit()
  }

  validateFile(file) {
    // Check file type
    if (!this.allowedTypes.includes(file.type)) {
      this.showError("Please select a valid image file (JPEG, PNG, GIF, or WebP)")
      return false
    }
    
    // Check file size
    if (file.size > this.maxFileSize) {
      this.showError("File size must be less than 5MB")
      return false
    }
    
    return true
  }

  createFileList(file) {
    const dt = new DataTransfer()
    dt.items.add(file)
    return dt.files
  }

  showPreview(file) {
    const reader = new FileReader()
    
    reader.onload = (e) => {
      this.previewImageTarget.src = e.target.result
      this.fileInfoTarget.textContent = `${file.name} (${this.formatFileSize(file.size)})`
      this.previewContainerTarget.classList.remove('hidden')
    }
    
    reader.readAsDataURL(file)
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  // Form submission
  handleSubmit(event) {
    if (!this.selectedFile) {
      event.preventDefault()
      this.showError("Please select a photo to upload")
      return
    }
    
    this.showProgress()
    this.disableSubmit()
  }

  // UI state management
  enableSubmit() {
    this.submitButtonTarget.disabled = false
    this.submitButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
  }

  disableSubmit() {
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
  }

  showProgress() {
    this.progressContainerTarget.classList.remove('hidden')
    this.previewContainerTarget.classList.add('hidden')
    
    // Simulate progress for better UX
    let progress = 0
    const interval = setInterval(() => {
      progress += Math.random() * 30
      if (progress > 90) progress = 90
      this.progressBarTarget.style.width = `${progress}%`
      
      if (progress >= 90) {
        clearInterval(interval)
      }
    }, 100)
  }

  showError(message) {
    this.errorMessageTarget.textContent = message
    this.errorContainerTarget.classList.remove('hidden')
    this.previewContainerTarget.classList.add('hidden')
    this.progressContainerTarget.classList.add('hidden')
    this.disableSubmit()
  }

  hideError() {
    this.errorContainerTarget.classList.add('hidden')
  }

  // Reset form state
  reset() {
    this.selectedFile = null
    this.fileInputTarget.value = ''
    this.previewContainerTarget.classList.add('hidden')
    this.progressContainerTarget.classList.add('hidden')
    this.hideError()
    this.disableSubmit()
    this.dropTextTarget.textContent = "Drag photo here or click to browse"
  }

  disconnect() {
    this.reset()
  }
}